import { defineStore } from "pinia";
import { computed } from "vue";
import { pickBy } from "lodash-es";
import { isSceneElementNode } from "./sceneElementFunctions";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useTemplateStore } from "../template/templates.store";
import { defaultElementAppearance } from "./sceneElementStoreHooks";
import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { Subset } from "@/helpers/utility/Type";
import type { SceneElementData } from "./SceneElementData";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";

export const useSceneElementStore = defineStore("model/sceneElements", () => {
  const sceneNodeStore = useSceneNodesStore();
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const indexStore = useIndexStore();

  async function create(options: Subset<SceneNodeItem<"element">>, data: Partial<SceneElementData> & (Partial<TargetItem | ResourceItem | IndexItem> | { elementUid: SceneElementData["elementUid"] })) {
    if (!("elementUid" in data) || !data.elementUid) {
      if (!("type" in data) || !data.type) {
        throw new Error("Element data must include 'type' when creating a new element.");
      }
      const store = getElementStoreByType(data.type);
      const element = await store.create(data as any);
      data.elementUid = element.uid;
    }

    const defaultAppearance = defaultElementAppearance;
    const filledData: SceneElementData = {
      elementUid: data.elementUid!,
      headlineHidden: options.data?.headlineHidden ?? data.headlineHidden ?? defaultAppearance.headlineHidden,

      imageId: options.data?.imageId ?? data.imageId ?? null,
      valueVisible: options.data?.valueVisible ?? data.valueVisible ?? defaultAppearance.valueVisible,
      barVisible: options.data?.barVisible ?? data.barVisible ?? defaultAppearance.barVisible,
      barWidthLimited: options.data?.barWidthLimited ?? data.barWidthLimited ?? defaultAppearance.barWidthLimited
    };

    return await sceneNodeStore.create({
      ...options,
      dataType: "element",
      data: filledData,
      gridLocked: true,
    });
  }

  const items = computed(() => {
    return pickBy(sceneNodeStore.items, isSceneElementNode);
  });

  const byUid = computed(() => {
    return pickBy(sceneNodeStore.byUid, isSceneElementNode);
  });

  function getElementStoreByType(type: "target" | "resource" | "index") {
    switch (type) {
      case "target":
        return targetStore;
      case "resource":
        return resourceStore;
      case "index":
        return indexStore;
    }
  }

  /**
   * get the first free element position
   */
  const freeElementPosition = computed<{
    x: number;
    y: number;
    id?: number;
  } | null>(() => {
    const templateStore = useTemplateStore();

    const template = templateStore.template;

    // if a template is active, return a free position of the template
    if (template && templateStore.freeElementPositions.length > 0) {
      return templateStore.freeElementPositions[0];
    }
    // return a good default position if the template is not active
    else {
      const defaultWidth = 200;
      const defaultHeight = 80;
      let x = 0;
      let y = 0;
      let nodeAtPosition;

      const nodes = useSceneNodesStore().orderedItems.filter((node) => node.dataType === "element");

      do {
        const right = x + defaultWidth;
        const bottom = y + defaultHeight;
        nodeAtPosition = nodes.find(
          (node) =>
            node.x <= right &&
            node.x + node.width >= x &&
            node.y <= bottom &&
            node.y + node.height >= y
        );
        if (nodeAtPosition) {
          if (nodeAtPosition.x + nodeAtPosition.width > 1000) {
            x = 0;
            y = nodeAtPosition.y + nodeAtPosition.height + 60;
          } else {
            x = nodeAtPosition.x + nodeAtPosition.width + 50;
          }
        }
      } while (nodeAtPosition != null);
      return {
        x,
        y
      };
    }
  });

  function $reset() {
    // handled by sceneNodeStore.
    return;
  }

  return {
    create,
    items,
    byUid,
    freeElementPosition,
    $reset
  }
});
